import { ObservableQuery, useQuery } from '@apollo/client'
import { uniqBy } from 'lodash'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import {
  LGProfileCompleteness,
  RetailerProfilePageQueryDocument,
  RetailerProfileTitleQueryDocument,
} from '__generated__/atlas-types'

import { fromGlobalId, toGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'
import { isAccountTypeBrand } from 'store/currentUser/selectors'

import { RETAILER_PROFILE } from 'routes/paths'

import { RetailerProfile } from './retailerProfilePage.types'
import { normalizeBrandCarriedName } from 'features/LeadGen/brandCarriedName.utils'

type BrandsCarriedApiNode = {
  accountId?: string | null
  name?: string | null
}

const mapBrandsCarriedToUi = (
  brandsCarried: readonly BrandsCarriedApiNode[] | null | undefined,
) =>
  uniqBy(
    (brandsCarried ?? [])
      .map((brandCarried) => {
        const hasJoorAccount = Boolean(
          fromGlobalId(brandCarried.accountId ?? '')?.id,
        )
        const name = brandCarried.name ?? ''
        if (!name) return null
        if (hasJoorAccount) {
          return { id: brandCarried.accountId ?? '', name }
        }
        const normalizedName = normalizeBrandCarriedName(name)
        return { id: normalizedName, name: normalizedName }
      })
      .filter(Boolean) as { id: string; name: string }[],
    'id',
  )

atlas`#graphql
  query RetailerProfileTitleQuery ($retailerId: ID!) {
    retailerProfile (id: $retailerId) {
      id
      displayName
      name
    }
  }`

type UseRetailerTitleHookReturn = {
  retailerName: string
}

export function useRetailerTitle(path: string): UseRetailerTitleHookReturn {
  const { accountId } = useParams<{ accountId: string }>()
  const retailerId = accountId ? toGlobalId('Retailer', accountId) : undefined
  const { data } = useQuery(RetailerProfileTitleQueryDocument, {
    client: atlasClient,
    variables: {
      retailerId: retailerId || '',
    },
    skip: path !== RETAILER_PROFILE || !retailerId,
  })

  return {
    retailerName:
      data?.retailerProfile?.displayName ||
      data?.retailerProfile?.name ||
      'Retailer Profile',
  }
}

atlas`#graphql
  query RetailerProfilePageQuery ($retailerId: ID!, $isBrandAccount: Boolean!) {
    retailerProfile (id: $retailerId) {
      id
      description
      name
      displayName
      profileSnoozeCounter
      autoConnect
      connectionStatus
      profileCompleteness
      logo {
        id
        url
      }
      websiteUrl
      taxId
      taxIdCountry
      yearsOrdered
      brandsOrdered
      categoriesOrdered
      socialMediaAccounts {
        type
        url
      }
      profileImages {
        id
        upload {
          id
          name
          url
          mediaType
        }
      }
      primaryLocation {
        id
        name
        address1
        address2
        city
        state
        country
        zip
        phone
        type
        wholesaleRange {
          min
          max
        }
        clienteleInfo {
          age
          male
          female
          descriptions {
            id
            name
          }
          interests {
            id
            name
          }
        }
        brandsCarried {
          id
          name
          accountId
        }
        categories {
          id
          name
        }
        yearEstablished
      }
      locations {
        id
        name
        address1
        address2
        city
        state
        country
        zip
        phone
        type
        wholesaleRange {
          min
          max
        }
        clienteleInfo {
          age
          male
          female
          descriptions {
            id
            name
          }
          interests {
            id
            name
          }
        }
        brandsCarried {
          id
          name
          accountId
        }
        categories {
          id
          name
        }
        yearEstablished
      }
      people {
        id
        name
        jobTitle
        profilePictureUrl
        phone
        submissions  @include(if: $isBrandAccount) {
          id
          createdAt
          messageId
          senderAccount {
            accountName
            accountUserName
          }
          }
        }
        accountSubmission  @include(if: $isBrandAccount){
          id
          messageId
          createdAt
          senderAccount {
            accountName
            accountUserName
          }
        }
        awards{
          id
          description
          awardBadgeUrl
        }
      }
    }
`

type UseRetailerDataHookReturn = {
  retailer: RetailerProfile
  loading: boolean
  refetchProfileData: ObservableQuery['refetch']
}

export const useRetailerData = (): UseRetailerDataHookReturn => {
  const isBrandAccount = useSelector<object, boolean>(isAccountTypeBrand)
  const { accountId } = useParams<{ accountId: string }>()
  const retailerId = toGlobalId('Retailer', accountId)
  const {
    data,
    loading,
    refetch: refetchProfileData,
  } = useQuery(RetailerProfilePageQueryDocument, {
    client: atlasClient,
    variables: {
      retailerId: retailerId || '',
      isBrandAccount,
    },
    skip: !retailerId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  })
  const retailerProfile = data?.retailerProfile

  const primaryLocation = retailerProfile?.primaryLocation
    ? {
        ...retailerProfile.primaryLocation,
        brandsCarried: mapBrandsCarriedToUi(
          retailerProfile.primaryLocation.brandsCarried ?? [],
        ),
      }
    : null

  const formattedLocations = retailerProfile?.locations?.map(
    (location: any) => ({
      ...location,
      brandsCarried: mapBrandsCarriedToUi(location?.brandsCarried ?? []),
    }),
  )

  return {
    retailer: {
      ...retailerProfile,
      profileCompleteness:
        retailerProfile?.profileCompleteness || LGProfileCompleteness.EMPTY,
      id: retailerProfile?.id || '',
      displayName: retailerProfile?.displayName || '',
      name: retailerProfile?.name,
      primaryLocation,
      locations: formattedLocations ?? [],
      people: retailerProfile?.people ?? [],
    },
    loading,
    refetchProfileData,
  }
}
